import { addLogoToPdf } from "helpers";
import { t } from "i18next";
import { jsPDF as JsPDF } from "jspdf";
import moment from "moment";

import { ADMIN_DELIVERIES_LABELS } from "../../constants";
import {
  DeliveriesLabels,
  DriverDeliveriesListItem,
  DriverInDeliveriesListItem
} from "../../financial.types";
import { registerReportFont } from "../reportFont";
import { getFonts } from "./getFonts";
import { renderDeliveriesTable } from "./renderDeliveriesTable";
import { renderSummary } from "./renderSummary";
import { renderTitleAndDateRange } from "./renderTitleAndDateRange";

export interface DownloadOneDriverDeliveriesReportData {
  driver: DriverInDeliveriesListItem;
  deliveries: DriverDeliveriesListItem[];
  dateRange: {
    from: string;
    to: string;
  };
  labels?: DeliveriesLabels;
}

export const downloadOneDriverDeliveriesReport = async ({
  driver,
  deliveries,
  dateRange,
  labels = ADMIN_DELIVERIES_LABELS
}: DownloadOneDriverDeliveriesReportData) => {
  const fromDate = moment(dateRange.from);
  const toDate = moment(dateRange.to);
  const isDateRangeSelected = toDate.diff(fromDate, "years") < 10;

  const fileDateLabel = isDateRangeSelected
    ? `${fromDate.format("DD_MM_YYYY")} - ${toDate.format("DD_MM_YYYY")}`
    : t("All time");

  const pdf = new JsPDF({
    unit: "px",
    orientation: "landscape"
  });

  addLogoToPdf(pdf);

  const font = await registerReportFont(pdf);

  const { normalFontSize, normalFontStyles, bigFontSize } = getFonts(font);

  pdf.setLineHeightFactor(1.3);

  renderTitleAndDateRange({
    pdf,
    normalFontSize,
    bigFontSize,
    driverName: `${driver.firstname} ${driver.lastname}`,
    font,
    dateRange: isDateRangeSelected ? { fromDate, toDate } : undefined
  });

  renderSummary({ pdf, normalFontStyles, driver, labels });

  renderDeliveriesTable({ pdf, normalFontStyles, deliveries, labels, font });

  const totalPages = pdf.getNumberOfPages();

  if (totalPages > 1) {
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      pdf.setFontSize(10);

      pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, {
        align: "center"
      });
    }
  }

  pdf.save(
    `DeliveriesReport-${driver.firstname}_${driver.lastname}-${fileDateLabel}.pdf`
  );
};
